import { useState } from 'react';
import {
    guardarEducacionDeEmpleado,
    obtenerDocumentosPorEmpleado,
    eliminarDocumento,
    getDocumentoCabByEmpleadoYTipo,
    guardarDocumentosCab,
    modificarDocumentosCab,
    anulaDocumentoCab
} from '../../../services/empleado/empleadoDocumentoService';
import { formatToJava } from '../../../util/dateUtils';

// Helper de fechas (lo copiamos para que el hook sea autónomo)
const convertirFecha = (f) => {
    if (!f) return null;
    if (Array.isArray(f)) return new Date(f[0], f[1] - 1, f[2]);
    if (typeof f === 'object' && f.year) return new Date(f.year, f.monthValue - 1, f.dayOfMonth);
    return new Date(f);
};

const emptyEducacion = {
    id: null,
    categoria: null,
    institucion: '',
    descripcion: '',
    fechaInicio: null,
    fechaFin: null,
    categoriaId: null
};

// Recibimos 'configMap' y 'listas' para poder filtrar correctamente los documentos
export const useEducacion = ({ empleadoId, toast, configMap, listas }) => {
    // 1. Estados
    const [educacionList, setEducacionList] = useState([]);
    const [educacionData, setEducacionData] = useState(emptyEducacion);
    const [educacionDialog, setEducacionDialog] = useState(false); // Modal del Formulario
    const [educacionFile, setEducacionFile] = useState([]);

    const [loading, setLoading] = useState(false);

    const [guardandoEdu, setGuardandoEdu] = useState(false)

    // 2. Carga (Lógica movida desde useEmpleados)
    const recargarEducacion = async (id) => {
        if (!id) {
            setEducacionList([]);
            return;
        }

        try {
            const docs = await obtenerDocumentosPorEmpleado(id);

            // Lógica de Filtrado (Idéntica a la que tenías)
            const configEdu = listas?.tiposOtrosDocs?.find(
                (d) => d.codigo === configMap.FORM_EDUCACION
            );
            let ID_EDU = configEdu ? configEdu.configuracionDetId : null;

            // Fallback: Si no hay ID de config, buscar por descripción
            if (!ID_EDU && docs.length > 0) {
                const posible = docs.find(
                    (d) =>
                        d.descripcion &&
                        (d.descripcion.includes('Universidad') ||
                            d.descripcion.includes('Colegio') ||
                            d.descripcion.includes('Instituto') ||
                            d.descripcion.includes('Escuela'))
                );
                if (posible) ID_EDU = posible.tipoDocumentoId;
            }

            if (ID_EDU || docs.length > 0) {
                const misEdu = docs.filter(
                    (d) =>
                        (ID_EDU && d.tipoDocumentoId === ID_EDU) ||
                        (!ID_EDU && d.descripcion && d.descripcion.includes(' - '))
                );

                setEducacionList(
                    misEdu.map((doc) => {
                        const partes = doc.descripcion ? doc.descripcion.split(' - ') : ['', ''];
                        return {
                            id: doc.id,
                            categoria: doc.categoriaId,
                            institucion: partes[0] || doc.descripcion,
                            titulo_nombre: partes[1] || '',
                            fechaInicio: convertirFecha(doc.fechaInicio),
                            fechaFin: convertirFecha(doc.fechaFin),
                            archivoUrl: doc.url
                        };
                    })
                );
            }
        } catch (e) {
            console.error('Error recargando educación:', e);
        }
    };

    // 3. Acciones del Modal
    const openNewEducacion = () => {
        setEducacionData(emptyEducacion);
        setEducacionFile(null);
        setEducacionDialog(true);
    };

    const openEditEducacion = (rowData) => {
        console.log ('EDIT...: ', rowData)
        setEducacionData({
            ...rowData,
            // Aseguramos que los nombres de campos coincidan con el input del form
            categoria_id: rowData.categoria,
            fecha_inicio: rowData.fechaInicio,
            fecha_fin: rowData.fechaFin
        });
        setEducacionFile(null); // Reset archivo al editar
        setEducacionDialog(true);
    };

    // 4. Guardar
    const saveEducacion = async () => {
        if (loading) return;
        if (!empleadoId) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Guarde el empleado primero.'
            });
            return;
        }

        // Nota: Ajusta 'categoria_id' vs 'categoria' según uses en tu form.
        // Aquí uso 'categoria_id' porque así lo tienes en el DialogoEmpleado.
        if (!educacionData.categoria_id || !educacionData.institucion) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Institución y Nivel son obligatorios.'
            });
            return;
        }

        // Validación de archivo solo para nuevos (si id es null)
        if (!educacionData.id && !educacionFile) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'El archivo es obligatorio para registros nuevos.'
            });
            return;
        }

        const formatearFecha = (d) => (d && d.toISOString ? d.toISOString().split('T')[0] : null);

        const payload = {
            id: educacionData.id,
            codigoTipoDocumento: configMap.FORM_EDUCACION,
            categoriaId: educacionData.categoria_id,
            referenciaDetalle: educacionData.numero || '', // Por si usas número
            institucion: educacionData.institucion,
            titulo_nombre: educacionData.titulo_nombre || educacionData.descripcion,
            fechaInicio: formatearFecha(educacionData.fecha_inicio),
            fechaFin: formatearFecha(educacionData.fecha_fin)
        };

        try {
            setLoading(true);
            await guardarEducacionDeEmpleado(empleadoId, payload, educacionFile);
            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Educación guardada.'
            });

            await recargarEducacion(empleadoId);
            setEducacionDialog(false);
            setEducacionData(emptyEducacion);
            setEducacionFile(null);
        } catch (e) {
            console.error(e);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar educación.'
            });
        } finally {
            setLoading(false);
        }
    };

    // 5. Eliminar
    const deleteEducacion = async (rowData) => {
        if (!window.confirm(`¿Estás seguro de eliminar el registro de ${rowData.institucion}?`))
            return;

        try {
            await eliminarDocumento(rowData.id);
            toast.current.show({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Registro eliminado.'
            });
            await recargarEducacion(empleadoId); // Recarga real
        } catch (error) {
            console.error('Error al eliminar educación:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar.'
            });
        }
    };

    /// codigo nuevo BEGIN
    const cargaEducacion = async (empleadoId) => {
        const CODE_TIPOREGISTRO_EDUCACION = 'NOM-0000-0015-0002';
        try {
            const data = await getDocumentoCabByEmpleadoYTipo(
                empleadoId,
                CODE_TIPOREGISTRO_EDUCACION
            );
            const lista = data.map((item) => ({
                id: item.documentoCabId,
                categoria: item.categoriaConfDet.configuracionDetId,
                institucion: item.referenciaDetalle,
                titulo_nombre: item.descripcion,
                fechaInicio: convertirFecha(item.fechaInicio),
                fechaFin: convertirFecha(item.fechaFin),
                archivoUrl: ''
            }));
            setEducacionList(lista);
        } catch (error) {
            console.error('Error al Obtener Licencias:', error);
        }
    };

    const guardaEducacion = async () => {
        const CODE_TIPOREGISTRO_EDUCACION = 'NOM-0000-0015-0002';
        setGuardandoEdu(true)
        if (loading) return;
        if (!empleadoId) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Guarde el empleado primero.'
            });
            return;
        }

        // Nota: Ajusta 'categoria_id' vs 'categoria' según uses en tu form.
        // Aquí uso 'categoria_id' porque así lo tienes en el DialogoEmpleado.
        if (!educacionData.categoriaId || !educacionData.institucion) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Institución y Nivel son obligatorios.'
            });
            return;
        }

        // Validación de archivo solo para nuevos (si id es null)
        if (!educacionData.id && !educacionFile) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'El archivo es obligatorio para registros nuevos.'
            });
            return;
        }

        try {
            const objGuardar = {
                entidadId: empleadoId,
                nombreEntidad: 'EMPLEADO',
                categoriaConfDetId: educacionData.categoriaId,
                referenciaDetalle: educacionData.institucion,
                descripcion: educacionData.titulo_nombre,
                fechaInicio: formatToJava(educacionData.fecha_inicio),
                fechaFin: formatToJava(educacionData.fecha_fin),
                fechaEmision: '', 
                codigoTipoDocumento: CODE_TIPOREGISTRO_EDUCACION
            }
            if (educacionData.id) {
                //console.log('MODIFICACION.....', objGuardar)
                objGuardar['documentoCabId'] = educacionData.id;
                const data = await modificarDocumentosCab(objGuardar, educacionFile);
            } else {
                const data = await guardarDocumentosCab(objGuardar, educacionFile);
            }
            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Licencia guardada correctamente'
            });
            await cargaEducacion(empleadoId);
            setGuardandoEdu(false)
            setEducacionDialog(false);
            setEducacionData(emptyEducacion);
            setEducacionFile(null);
        } catch (error) {
            console.error('Error al Guardar Educacion', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se han guardado las Licencias'
            });
        }
        
    }

    const anulaEducacion = async (rowData) => {
        console.log ('AFINAMIENTO.', rowData)
            if (!window.confirm(`¿Estás seguro de eliminar el registro de ${rowData.institucion}?`)) return;
    
            try {
                const data = await anulaDocumentoCab(rowData.id);
                toast.current.show({
                    severity: 'success',
                    summary: 'Eliminado',
                    detail: 'Licencia eliminada.'
                });
    
                // Actualización optimista o recarga
                setEducacionList((prev) => prev.filter((item) => item.id !== rowData.id));
            } catch (error) {
                console.error('Error eliminando licencia:', error);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo eliminar.'
                });
            }
        };

    /// codigo nuevo END
    return {
        educacionList,
        educacionData,
        setEducacionData,
        educacionDialog,
        setEducacionDialog,
        educacionFile,
        setEducacionFile,
        recargarEducacion,
        openNewEducacion,
        openEditEducacion,
        saveEducacion,
        deleteEducacion,
        setEducacionList,
        loading,
        cargaEducacion,
        guardaEducacion,
        guardandoEdu,
        anulaEducacion
    };
};
